import type { Point } from "highcharts";
import type Highcharts from "highcharts";
import { renderToString } from "react-dom/server";

import { ChangeCell, type ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { CountryCell } from "app/components/React/Table/cells";
import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { translate } from "app/filters/i18nFilter";
import { countryByIdFilter } from "app/filters/ngFilters";

import { NA_VALUE } from "../../utils/utils";
import { formatPercentValue } from "../utils/utils";
import type { ICountriesResponse } from "../hooks/useCountries";

export const PAGE_SIZE = 5;
export const PREVIEW_ITEM_COUNT = 5;

const CHANGE_COLUMN_CONFIG = {
    THRESHOLD_FOR_MAX_DISPLAY: 50,
} as const;

const MAX_CHANGE_LABEL = " > 5000%";

const WINDOW_WIDTH_BREAKPOINTS = {
    LARGE: 1700,
    MEDIUM: 1440,
} as const;

const MAP_DIMENSIONS = {
    LARGE: { width: 647, height: 320 },
    MEDIUM: { width: 491, height: 243 },
    SMALL: { width: 401, height: 203 },
} as const;

export type CountryData = ICountriesResponse["countrySpendShares"][number];

export type MapDataPoint = {
    "hc-key": string;
    value: number;
};

export const getMapDimensionsByWindowWidth = (windowWidth: number) => {
    if (windowWidth > WINDOW_WIDTH_BREAKPOINTS.LARGE) {
        return MAP_DIMENSIONS.LARGE;
    }
    if (windowWidth > WINDOW_WIDTH_BREAKPOINTS.MEDIUM) {
        return MAP_DIMENSIONS.MEDIUM;
    }
    return MAP_DIMENSIONS.SMALL;
};

export const getMapConfig = (mapDimensions: ReturnType<typeof getMapDimensionsByWindowWidth>): Highcharts.Options => {
    return {
        chart: {
            height: mapDimensions.height,
            width: mapDimensions.width,
            animation: false,
        },
        title: {
            text: null,
        },
        tooltip: {
            useHTML: true,
            formatter: function (this: Point) {
                return renderToString(
                    <Stack gap="8px">
                        <Text
                            fontVariant="subtitle2"
                            color={colorsPalettes.carbon[400]}
                        >
                            {this.name}
                        </Text>
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[400]}
                        >
                            {translate("ad.intelligence.overview.countries.map.tooltip.spend.rank")}{" "}
                            <strong>#{this.index + 1}</strong>
                        </Text>
                    </Stack>,
                );
            },
        },
    };
};

export const getColumnConfig = (currentPage: number, withIndexColumn = false): ColumnsType<CountryData> => {
    const indexCellProps = getIndexCellPropsForTableV2(currentPage, PAGE_SIZE, undefined, 80);

    const indexColumn = {
        ...indexCellProps,
        fixed: undefined,
        resize: false,
        title: translate("ad.intelligence.overview.countries.table.rank"),
    } as ColumnsType<CountryData>[number];

    const columns: ColumnsType<CountryData> = [
        {
            dataIndex: "country",
            title: translate("ad.intelligence.overview.countries.table.country"),
            resize: false,
            render: (value: number) => <CountryCell value={value} />,
        },
        {
            dataIndex: "popChange",
            title: translate("ad.intelligence.overview.countries.table.popChange"),
            width: 120,
            align: "right",
            resize: false,
            render: (value: number | null) => {
                const formattedValue =
                    value == null
                        ? NA_VALUE
                        : value >= CHANGE_COLUMN_CONFIG.THRESHOLD_FOR_MAX_DISPLAY
                          ? MAX_CHANGE_LABEL
                          : formatPercentValue(value);

                return (
                    <ChangeCell
                        value={value}
                        noChangeSign={NA_VALUE}
                        formattedValue={formattedValue}
                    />
                );
            },
        },
    ];

    return withIndexColumn ? [indexColumn, ...columns] : columns;
};

export const transformCountryDataToMapPoints = (countryData: CountryData[]): MapDataPoint[] => {
    const getCountryById = countryByIdFilter();

    return countryData
        .map(({ country }, index, arr) => {
            const countryObj = getCountryById(country);
            return {
                "hc-key": countryObj.code,
                value: arr.length - index,
            };
        })
        .filter((item): item is MapDataPoint => item["hc-key"] != null && item.value != null);
};
